// src/contexts/DomainContext.js
//
// Phase 1 — Trust Domain Foundation (frontend)
//
// Provides the COMMUNITY / CHURCH trust domain context, context switching,
// and the list of churches the current user can switch into. This powers
// the domain-aware navigation in LeftNav and the DomainSwitcher control.
//
// Per Governance Rule #2, Community and Church are first-class trust
// domains that "share identity but not visibility" — switching domains
// changes which navigation/context is shown, not the user's identity.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery, gql } from "@apollo/client";

export const TRUST_DOMAINS = Object.freeze({
  COMMUNITY: "COMMUNITY",
  CHURCH: "CHURCH",
});

const DOMAIN_STORAGE_KEY = "lifecircle.trustDomain";
const CHURCH_STORAGE_KEY = "lifecircle.activeChurchId";

const DomainContext = createContext(null);

export const useDomain = () => useContext(DomainContext);

const GET_MY_CHURCHES_FOR_DOMAIN = gql`
  query GetMyChurchesForDomain {
    myChurches {
      id
      name
      slug
      masterCircleId
    }
  }
`;

function readStoredDomain() {
  try {
    const stored = localStorage.getItem(DOMAIN_STORAGE_KEY);
    return stored === TRUST_DOMAINS.CHURCH ? TRUST_DOMAINS.CHURCH : TRUST_DOMAINS.COMMUNITY;
  } catch {
    return TRUST_DOMAINS.COMMUNITY;
  }
}

function readStoredChurchId() {
  try {
    return localStorage.getItem(CHURCH_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export const DomainProvider = ({ children }) => {
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  }, []);
  const userId = currentUser?.id;

  const [domain, setDomainState] = useState(readStoredDomain);
  const [activeChurchId, setActiveChurchIdState] = useState(readStoredChurchId);

  const { data } = useQuery(GET_MY_CHURCHES_FOR_DOMAIN, {
    skip: !userId,
    fetchPolicy: "cache-first",
  });

  const myChurches = data?.myChurches ?? [];

  // Guard against a stale activeChurchId (e.g. user removed from a church).
  useEffect(() => {
    if (!activeChurchId || myChurches.length === 0) return;
    const stillValid = myChurches.some((c) => c.id === activeChurchId);
    if (!stillValid) {
      setActiveChurchIdState(null);
      setDomainState(TRUST_DOMAINS.COMMUNITY);
    }
  }, [activeChurchId, myChurches]);

  const persistDomain = useCallback((value) => {
    setDomainState(value);
    try {
      localStorage.setItem(DOMAIN_STORAGE_KEY, value);
    } catch {
      // ignore storage failures (private browsing, etc.)
    }
  }, []);

  const persistActiveChurchId = useCallback((value) => {
    setActiveChurchIdState(value);
    try {
      if (value) {
        localStorage.setItem(CHURCH_STORAGE_KEY, value);
      } else {
        localStorage.removeItem(CHURCH_STORAGE_KEY);
      }
    } catch {
      // ignore storage failures
    }
  }, []);

  const switchToCommunity = useCallback(() => {
    persistDomain(TRUST_DOMAINS.COMMUNITY);
  }, [persistDomain]);

  const switchToChurch = useCallback(
    (churchId) => {
      if (churchId) {
        persistActiveChurchId(churchId);
      }
      persistDomain(TRUST_DOMAINS.CHURCH);
    },
    [persistActiveChurchId, persistDomain]
  );

  const activeChurch = useMemo(
    () => myChurches.find((c) => c.id === activeChurchId) || null,
    [myChurches, activeChurchId]
  );

  const value = useMemo(
    () => ({
      domain,
      isCommunity: domain === TRUST_DOMAINS.COMMUNITY,
      isChurch: domain === TRUST_DOMAINS.CHURCH,
      activeChurchId,
      activeChurch,
      myChurches,
      switchToCommunity,
      switchToChurch,
    }),
    [domain, activeChurchId, activeChurch, myChurches, switchToCommunity, switchToChurch]
  );

  return <DomainContext.Provider value={value}>{children}</DomainContext.Provider>;
};
